import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import '../pagesCSS/saved.css';
import '../pagesCSS/saved.mobile.css';

import companyDictionary from '../Assets/companyDictionary.json';
import companyLocations from '../Assets/locations.json';
import placeholderCompanyImage from '../Assets/Images/blank_profile.png';

function normalize(input) {
    return input
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^\w\s]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

const aliasToCompanyId = {};

Object.entries(companyDictionary).forEach(([companyId, company]) => {
  company.aliases.forEach(alias => {
    aliasToCompanyId[normalize(alias)] = companyId;
  });
});

const companies = Object.entries(companyDictionary).map(
  ([id, company]) => ({
    id,
    displayName: company.displayName
  })
);


const Saved = () => {
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('User');
  const [allEntries, setAllEntries] = useState([]);
  const [hasContributed, setHasContributed] = useState(false);
  const companyLocationsArray = Object.keys(companyLocations);
  const auth = getAuth();
  const db = getDatabase();
  const navigate = useNavigate();

  const checkUserContribution = async (userId) => {
    try {
      const userInterviewsRef = ref(db, `interviews/${userId}`);
      const snapshot = await get(userInterviewsRef);
      return snapshot.exists();
    } catch (error) {
      console.error("Error checking user contributions:", error);
      return false;
    }
  };
  

  const getCompanyLogo = (companyName) => {
    if (!companyName) return placeholderCompanyImage;

    const normalized = normalize(companyName);
    const companyId = aliasToCompanyId[normalized];

    if (!companyId || !companyDictionary[companyId]) {
      return placeholderCompanyImage;
    }

    const logoPath = companyDictionary[companyId].logo;

    try {
      return require(`../Assets/Images/CompanyImages/${logoPath}`);
    } catch {
      return placeholderCompanyImage;
    }
  };


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userId = currentUser.uid;
        setUser(currentUser);
        try {
          const userRef = ref(db, `users/${userId}/username`);
          const snapshot = await get(userRef);
          if (snapshot.exists()) {
            setUsername(snapshot.val());
          }
          const hasUserContributed = await checkUserContribution(userId);
          setHasContributed(hasUserContributed);
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUser(null);
        setUsername('User');
        setHasContributed(false);
        // Fetch entries even when user is not signed in
        fetchAllEntries();
      }
    });
    return () => unsubscribe();
  }, [auth, db]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date not available';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncateText = (text, wordLimit = 50) => {
    return text;
  };

  const fetchAllEntries = async () => {
    try {
      const interviewsRef = ref(db, 'interviews');
      const snapshot = await get(interviewsRef);

      if (snapshot.exists()) {
        const entries = [];
        snapshot.forEach((userSnapshot) => {
          userSnapshot.forEach((entrySnapshot) => {
            entries.push({
              id: entrySnapshot.key,
              userId: userSnapshot.key,
              ...entrySnapshot.val(),
            });
          });
        });
        entries.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setAllEntries(entries);
        setFilteredEntries(entries);
      }
    } catch (error) {
      console.error("Error fetching entries:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchAllEntries();
    }
  }, [user, db]);
  

  const [filteredEntries, setFilteredEntries] = useState(allEntries);
  const [filters, setFilters] = useState({
    company: "Company",
    position: "Position",
    location: "Location"
  });

  useEffect(() => {
    setFilteredEntries(allEntries);
  }, [allEntries]);

  const applyFilters = (updatedFilters) => {
    let filtered = [...allEntries];
  
    if (updatedFilters.company !== "Company") {
      filtered = filtered.filter(entry => {
        const entryCompanyId =
          aliasToCompanyId[normalize(entry.companyName)];

        return entryCompanyId === updatedFilters.company;
      });
    }
  
    if (updatedFilters.position !== "Position") {
      filtered = filtered.filter(entry => {
        const entryPosition = (entry.positionType || '').trim().toLowerCase();
        const filterPosition = updatedFilters.position.trim().toLowerCase();
        
        // Normalize for comparison (handles Full-Time, full-time, Full Time, etc.)
        const normalizedEntry = entryPosition.replace(/[-\s]/g, '');
        const normalizedFilter = filterPosition.replace(/[-\s]/g, '');
        
        return normalizedEntry === normalizedFilter;
      });
    }
  
    if (updatedFilters.location !== "Location") {
      filtered = filtered.filter(
        entry => entry.location.trim().toLowerCase() === updatedFilters.location.toLowerCase()
      );
    }
  
    setFilteredEntries(filtered);
  };
  
  
  const handleCompanyFilterChange = (event) => {
    const updatedFilters = { ...filters, company: event.target.value };
    console.log('Company filter changed to:', event.target.value);
  console.log('All entries count:', allEntries.length);
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
  };

  const handlePositionFilterChange = (event) => {
    const updatedFilters = { ...filters, position: event.target.value };
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
  };
  
  const handleLocationFilterChange = (event) => {
    const updatedFilters = { ...filters, location: event.target.value };
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
  };

  const removeCompanyFilter = () => {
    const updatedFilters = { ...filters, company: "Company" };
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
    document.getElementById("company-filter").value = "Company";
  };
  
  const removePositionFilter = () => {
    const updatedFilters = { ...filters, position: "Position" };
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
    document.getElementById("position-filter").value = "Position";
  };
  
  const removeLocationFilter = () => {
    const updatedFilters = { ...filters, location: "Location" };
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
    document.getElementById("location-filter").value = "Location";
  };

  // for expanding the card for more details
  const [expandedCardId, setExpandedCardId] = useState(null);

  const toggleCardExpansion = (cardId) => {
    if (!user) {
      // Redirect to sign in if not logged in
      navigate('/userAuth');
      return;
    }
    
    if (!hasContributed) {
      // Don't expand if user hasn't contributed
      navigate('/list');
      return;
    }
    
    setExpandedCardId((prevId) => (prevId === cardId ? null : cardId));
  };
  
  // Function to render filter bar
  const renderFilterBar = () => (
    <div className="filter-bar">
      <div className='filter-select-container' >
        <select className="filter-select" onChange={handleCompanyFilterChange} id='company-filter' defaultValue="Company">
          <option value="Company">Company</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}> {company.displayName} </option>
          ))}
        </select>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 15 16" fill="none">
          <path d="M7.36491 9.21336L10.424 6.15423H4.30579L7.36491 9.21336Z" fill="#484848"/>
        </svg>
      </div>
      
      <div className='filter-select-container'>
        <select className="filter-select" id='position-filter'  onChange={handlePositionFilterChange} defaultValue="Position">
          <option value="Position">Position Type</option>
          <option value="Internship">Internship</option>
          <option value="Full-Time">Full-Time</option>
          <option value="Co-Op">Co-Op</option>
        </select>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 15 16" fill="none">
          <path d="M7.36491 9.21336L10.424 6.15423H4.30579L7.36491 9.21336Z" fill="#484848"/>
        </svg>
      </div>

      <div className='filter-select-container' >
        <select className="filter-select" id='location-filter' onChange={handleLocationFilterChange} defaultValue="Location">
          <option value="Location">Location</option>
          {companyLocationsArray.map((location) => (
            <option key={location} value={location}>{location}</option>
          ))}
        </select>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 15 16" fill="none">
          <path d="M7.36491 9.21336L10.424 6.15423H4.30579L7.36491 9.21336Z" fill="#484848"/>
        </svg>
      </div>
      
      
    </div>
  );

  // Function to render active filters
  const renderActiveFilters = () => (
    filteredEntries.length !== allEntries.length && (
      <div className="active-filters">
        {filters.company !== "Company" && (
          <span className="filter-tag">
            {filters.company}
            <button onClick={removeCompanyFilter} className="remove-filter-btn">x</button>
          </span>
        )}
        {filters.position !== "Position" && (
          <span className="filter-tag">
            {filters.position}
            <button onClick={removePositionFilter} className="remove-filter-btn">x</button>
          </span>
        )}
        {filters.location !== "Location" && (
          <span className="filter-tag">
            {filters.location}
            <button onClick={removeLocationFilter} className="remove-filter-btn">x</button>
          </span>
        )}
      </div>
    )
  );

  // Function to render entries count
  const renderEntriesCount = () => (
    filteredEntries.length === 0 ? (
      <div className="numEntries">No entries found...</div>
    ) : (
      <div className="numEntries">{filteredEntries.length} entries found</div>
    )
  );

  // Function to render entries for non-logged in users - show basic info only
  const renderPublicEntries = () => (
    <div className="entries-grid">
      {filteredEntries.map((entry) => {
        const logoSrc = getCompanyLogo(entry.companyName);

        
        return (
          <div key={entry.id}>
            <div className="entry-card">                
              <div className="entry-header">
                <div className="header-left">
                  <img className="company-logo"
                    src={logoSrc}
                    alt={`${entry.companyName} logo`}
                    onError={(e) => e.target.src = placeholderCompanyImage}
                  />
                  <div className="title-and-location">
                    <h3 className="company-name">
                      {entry.companyName}, {entry.roleTitle}
                    </h3>
                    <p className="location-and-type">
                      {entry.location} • {entry.positionType}
                    </p>
                  </div>
                </div>
                <div className="header-right">
                  Posted on {formatDate(entry.timestamp)}
                </div>
              </div>

              <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '15px'}}>
                <div style={{width: '50%'}}>
                  <div className="detail-item">
                    <strong>GPA:</strong> {entry.gpa}
                  </div>
                  <div className="detail-item">
                    <strong>Year Applied:</strong> {entry.yearApplied}
                  </div>
                </div>
                <div style={{width: '50%'}}>
                  <div className="detail-item">
                    <strong>Hourly Rate:</strong> {entry.hourlyRate || 'idk'}
                  </div>
                  <div className="detail-item">
                    <strong>Base Salary:</strong> {entry.baseSalary || 'idk'}
                  </div>
                </div>
              </div>
              
              <div className="see-more-details">
                <span className="details-link" onClick={() => toggleCardExpansion(entry.id)}>
                  <div className="details-text">See more details</div>
                  <div className="down-arrow">▼</div>
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Function to render entries for logged in users
  const renderMemberEntries = () => (
    <div className="entries-grid">
      {filteredEntries.map((entry) => {
        const logoSrc = getCompanyLogo(entry.companyName);

        
        return (
          <div key={entry.id}>
            <div className="entry-card">                
              <div className="entry-header">
                <div className="header-left">
                  <img className="company-logo"
                    src={logoSrc}
                    alt={`${entry.companyName} logo`}
                    onError={(e) => e.target.src = placeholderCompanyImage}
                  />
                  <div className="title-and-location">
                    <h3 className="company-name">
                      {entry.companyName}, {entry.roleTitle}
                    </h3>
                    <p className="location-and-type" style={{color: '#403c43'}}>
                      {entry.location} • {entry.positionType}
                    </p>
                  </div>
                </div>
                <div className="header-right">
                  Posted on {formatDate(entry.timestamp)}
                </div>
              </div>

              <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '15px'}}>
                <div style={{width: '50%'}}>
                  <div className="detail-item">
                    <strong>GPA:</strong> {entry.gpa}
                  </div>
                  <div className="detail-item">
                    <strong>Year Applied:</strong> {entry.yearApplied}
                  </div>
                </div>
                <div style={{width: '50%'}}>
                  <div className="detail-item">
                    <strong>Hourly Rate:</strong> {entry.hourlyRate || 'idk'}
                  </div>
                  <div className="detail-item">
                    <strong>Base Salary:</strong> {entry.baseSalary || 'idk'}
                  </div>
                </div>
              </div>

            

              {expandedCardId === entry.id ? (
                // Expanded view - show additional details
                <div style={{marginTop: '15px'}}>
                  <div className="interview-process">
                    <strong>Interview Process:</strong>
                    <p className="process-text">{entry.interviewProcess}</p>
                  </div>
                  <div className="questions-asked">
                    <strong>Questions Asked:</strong>
                    <p className="questions-text">{entry.questionsAsked}</p>
                  </div>

                  {entry.openToContact && (
                    <>
                      <div className="detail-item">
                        <strong>Name:</strong> {entry.name || 'N/A'}
                      </div>
                      <div className="detail-item">
                        <strong>Contact:</strong> {entry.contact || 'N/A'}
                      </div>
                    </>
                  )}

                  
                  
                  <div style={{marginTop: '15px', backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '8px'}}>
                    <div className="detail-item">
                      <strong>Application Method:</strong> {entry.applicationMethod || 'N/A'}
                    </div>
                    <div className="detail-item">
                      <strong>Hiring Process Length:</strong> {entry.hiringProcessLength || 'N/A'}
                    </div>
                    <div className="detail-item">
                      <strong>Total Compensation:</strong> {entry.totalCompensation || 'N/A'}
                    </div>
                  </div>
                </div>
              ) : null}
              
              <div className="see-more-details">
                <span className="details-link" onClick={() => toggleCardExpansion(entry.id)}>
                  <div className="details-text">{expandedCardId === entry.id ? "Hide details" : "See more details"}</div>
                  <div className={`down-arrow ${expandedCardId === entry.id ? 'rotated' : ''}`}>▼</div>
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="savedBody">
      {user ? (
        hasContributed ? (
          // Full experience for logged-in users who have contributed
          <div className="entries-page">
            <h1 className="page-title">All Interview Experiences</h1>
            
          <div className="filters-container">
            {renderFilterBar()}
          </div>
            {renderActiveFilters()}
            {renderEntriesCount()}
          
            {renderMemberEntries()}
          </div>
        ) : (
          // Prompt to contribute for logged-in users who haven't contributed
        <div className="entries-page">
          <h1 className="page-title">All Interview Experiences</h1>
          <p className="contribute-message">
            Contribute something to see all the experiences posted by your peers.
          </p>

          {renderFilterBar()}
          {renderActiveFilters()}
          {renderEntriesCount()}
          
          {renderPublicEntries()}
        </div>
        )
      ) : (
        // Limited view for non-logged in users
        <div className="entries-page">
          <h1 className="page-title">All Interview Experiences</h1>
          <p className="contribute-message">
            Sign in to see detailed interview experiences and contribute your own.
          </p>

          {renderFilterBar()}
          {renderActiveFilters()}
          {renderEntriesCount()}
          
          {renderPublicEntries()}
        </div>
      )}
    </div>
  );
};

export default Saved;